import hostname from "./config.host";
import { fetchWrapper } from "./fetch-wrapper";
const apiUrl: string = `${hostname}/aladdin_api/v1`;

// POST (not GET) — the query needs every site the user has permission for,
// passed as a JSON array body, not a single URL query param. The URL keeps
// `type` and the first uid (matching the confirmed request shape from
// backend) alongside the full array in the body.
function getAllBrandsData(
  oauthTokenHeader: string,
  biz_layer_query_id: any,
  website_shared_uids: string[],
  type: any,
) {
  const primaryUid = website_shared_uids?.[0] ?? "";
  return fetchWrapper.post(
    `${apiUrl}/biz_layer_query/run/${biz_layer_query_id}/?website_shared_uid=${primaryUid}&type=${type}`,
    { website_shared_uid: website_shared_uids },
    oauthTokenHeader,
  );
}

function tryToConnect(oauthTokenHeader: string, data: { brands: number[] }) {
  return fetchWrapper.post(
    `${apiUrl}/partners/affiliate_accounts/try_to_connect`,
    data,
    oauthTokenHeader,
  );
}

// --- Below: ported verbatim from brand-config-report.endpoints.ts (same
// request structure/URLs) so the Brands module has its own copy and no
// longer needs to call brandConfigReportService. The legacy file is
// untouched and keeps serving the Brand Configuration Report module. ---

function editBrandProperties(
  oauthTokenHeader: string,
  data: any,
  brand_shared_uid: any,
) {
  return fetchWrapper.put(
    `${apiUrl}/partners/affiliate_accounts/brand_properties/${brand_shared_uid}/`,
    data,
    oauthTokenHeader,
  );
}

function postBrandConfigurationReport(oauthTokenHeader: string, data: any) {
  return fetchWrapper.post(
    `${apiUrl}/partners/affiliate_accounts/brands_integration`,
    data,
    oauthTokenHeader,
  );
}

function getBrandOverviewLastRuns(oauthTokenHeader: string, brand_id: string) {
  return fetchWrapper.get(
    `${apiUrl}/data_collection/dc_process_log/last_runs/?brand_id=${brand_id}&limit=10`,
    oauthTokenHeader,
  );
}

function getBrandOverviewAdvisorSettings(
  oauthTokenHeader: string,
  brand_id: string,
  report: string,
) {
  return fetchWrapper.get(
    `${apiUrl}/partners/affiliate_accounts/brands_integration/overview/advisor_settings/?brand_id=${brand_id}&report=${report}`,
    oauthTokenHeader,
  );
}

function getBrandOverviewUnifierSettings(
  oauthTokenHeader: string,
  brand_id: string,
  report: string,
) {
  return fetchWrapper.get(
    `${apiUrl}/partners/affiliate_accounts/brands_integration/overview/unifier_settings/?brand_id=${brand_id}&report=${report}`,
    oauthTokenHeader,
  );
}

function getBrandOverviewFetcherSettings(
  oauthTokenHeader: string,
  reqBody: any,
) {
  return fetchWrapper.post(
    `${apiUrl}/partners/affiliate_accounts/brands_integration/overview/fetcher_settings/`,
    reqBody,
    oauthTokenHeader,
  );
}

function brandsRunProcess(oauthTokenHeader: string, data: any) {
  return fetchWrapper.post(
    `${apiUrl}/operations/argo/template/`,
    data,
    oauthTokenHeader,
  );
}

function getAllConfigForFetcher(
  oauthTokenHeader: string,
  biz_layer_query_id: any,
  website_shared_uid: any,
) {
  return fetchWrapper.get(
    `${apiUrl}/biz_layer_query/run/${biz_layer_query_id}/?website_shared_uid=${website_shared_uid}`,
    oauthTokenHeader,
  );
}

function getOverviewPerformance(
  oauthTokenHeader: string,
  biz_layer_query_id: any,
  website_shared_uid: any,
  type: any,
  brand_id: any,
  start_date: any,
  end_date: any,
  limit: any,
) {
  return fetchWrapper.get(
    `${apiUrl}/biz_layer_query/run/${biz_layer_query_id}/?website_shared_uid=${website_shared_uid}&type=${type}&brand_id=${brand_id}&start_date=${start_date}&end_date=${end_date}&limit=${limit}`,
    oauthTokenHeader,
  );
}

function getObjectsData(
  oauthTokenHeader: string,
  biz_layer_query_id: any,
  website_shared_uid: any,
  type: any,
  brand_id: any,
) {
  return fetchWrapper.get(
    `${apiUrl}/biz_layer_query/run/${biz_layer_query_id}/?website_shared_uid=${website_shared_uid}&type=${type}&brand_id=${brand_id}`,
    oauthTokenHeader,
  );
}

function getBrandAccountOverviewInfo(
  oauthTokenHeader: string,
  biz_layer_query_id: any,
  website_shared_uid: any,
  type: any,
  brand_id: any,
) {
  return fetchWrapper.get(
    `${apiUrl}/biz_layer_query/run/${biz_layer_query_id}/?website_shared_uid=${website_shared_uid}&type=${type}&brand_id=${brand_id}`,
    oauthTokenHeader,
  );
}

function getRawTableData(
  oauthTokenHeader: string,
  table_id: string | any,
  start_date: string | any,
  end_date: string | any,
  type: string | any,
  fieldMappingId?: string | any,
) {
  const params = new URLSearchParams({
    table_id,
    start_date,
    end_date,
    type,
  });

  if (fieldMappingId) {
    params.append("field_mapping_id", fieldMappingId);
  }

  return fetchWrapper.get(
    `${apiUrl}/partners/affiliate_accounts/brands_integration/overview/raw_data/?${params.toString()}`,
    oauthTokenHeader,
  );
}

function getBrandBackendRequirements(
  oauthTokenHeader: string,
  backend_shared_uid: string,
) {
  return fetchWrapper.get(
    `${apiUrl}/data_collection/fetcher/backend_requirements/${backend_shared_uid}/`,
    oauthTokenHeader,
  );
}

function getBrandProperties(
  oauthTokenHeader: string,
  brand_shared_uid: string,
) {
  return fetchWrapper.get(
    `${apiUrl}/partners/affiliate_accounts/brand_properties/${brand_shared_uid}/`,
    oauthTokenHeader,
  );
}

function getTestAgainPopupData(
  oauthTokenHeader: string,
  biz_layer_query_id: any,
  website_shared_uid: any,
  type: any,
  brand_id: any,
) {
  return fetchWrapper.get(
    `${apiUrl}/biz_layer_query/run/${biz_layer_query_id}/?website_shared_uid=${website_shared_uid}&type=${type}&brand_id=${brand_id}`,
    oauthTokenHeader,
  );
}

function manuallyApprove(oauthTokenHeader: string, data: any, brand_id: any) {
  return fetchWrapper.put(
    `${apiUrl}/partners/affiliate_accounts/brands_integration/manual_approved/?brand_id=${brand_id}`,
    data,
    oauthTokenHeader,
  );
}

export const brandsService = {
  getAllBrandsData,
  tryToConnect,
  editBrandProperties,
  postBrandConfigurationReport,
  getBrandOverviewLastRuns,
  getBrandOverviewAdvisorSettings,
  getBrandOverviewUnifierSettings,
  getBrandOverviewFetcherSettings,
  brandsRunProcess,
  getAllConfigForFetcher,
  getOverviewPerformance,
  getObjectsData,
  getBrandAccountOverviewInfo,
  getRawTableData,
  getBrandBackendRequirements,
  getBrandProperties,
  getTestAgainPopupData,
  manuallyApprove,
};
